import { useNavigate } from "@tanstack/react-router";
import {
  Globe,
  Users,
  Edit,
  Link,
  Calendar,
  ChevronUp,
  ChevronDown,
  Copy,
  Check,
  Image,
  Palette,
} from "lucide-react";
import { useState } from "react";
import type { TenantRead } from "@harmonyso/tenants-sdk";
import { CELLS, tenantCellSearch } from "@/lib/cells/cells";

import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { EditTenantDialog } from "@/components/tenants/edit-tenant-dialog";
import { SsoSetupDialog } from "@/components/tenants/sso-setup-dialog";
import { LogoManagementDialog } from "@/components/tenants/logo-management-dialog";
import { PortalBrandingDialog } from "@/components/tenants/portal-branding-dialog";
import { HarmonyLogoIcon } from "@/components/icons/harmony-logo-icon";
import { TrialStatusDot } from "@/components/tenants/trial-status-dot";
import { getTrialIndicator } from "@/lib/trial";

// Cell columns appear only once there is more than one cell to tell apart.
const showCellColumns = CELLS.length > 1;
const columnsClass = showCellColumns
  ? "grid-cols-[minmax(250px,1fr)_minmax(180px,1fr)_180px_100px_100px_90px_110px_60px_140px_130px]"
  : "grid-cols-[minmax(250px,1fr)_minmax(180px,1fr)_180px_100px_100px_60px_140px_130px]";
const tableMinWidth = showCellColumns ? "1362px" : "1152px";

type SortField = "company" | "created_at";
type SortOrder = "asc" | "desc";

interface TenantsTableProps {
  tenants: TenantRead[];
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
}

export function TenantsTable({
  tenants,
  sortField,
  sortOrder,
  onSort,
}: TenantsTableProps) {
  const navigate = useNavigate();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [logoDialogTenant, setLogoDialogTenant] = useState<TenantRead | null>(
    null,
  );
  const [brandingDialogTenant, setBrandingDialogTenant] =
    useState<TenantRead | null>(null);

  const handleTenantClick = (tenant: TenantRead) => {
    void navigate({
      to: "/tenants/$tenantId",
      params: { tenantId: tenant.id },
      search: tenantCellSearch(tenant),
    });
  };

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => {
        setCopiedId(null);
      }, 2000);
    } catch (err) {
      console.error("Failed to copy: ", err);
    }
  };

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(date));
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return null;
    }
    return sortOrder === "asc" ? (
      <ChevronUp className="ml-1 h-3 w-3" />
    ) : (
      <ChevronDown className="ml-1 h-3 w-3" />
    );
  };

  return (
    <div className="overflow-x-auto rounded-lg border">
      <div
        className={`bg-muted/50 grid ${columnsClass} gap-3 px-4 py-4 text-sm font-medium`}
        style={{ minWidth: tableMinWidth }}
      >
        <div
          className="hover:text-foreground flex cursor-pointer items-center"
          onClick={() => {
            onSort("company");
          }}
        >
          Company
          {getSortIcon("company")}
        </div>
        <div>Domain</div>
        <div>ID</div>
        <div>Industry</div>
        <div>Size</div>
        {showCellColumns && (
          <>
            <div>Residency</div>
            <div>Cell</div>
          </>
        )}
        <div>Trial</div>
        <div
          className="hover:text-foreground flex cursor-pointer items-center"
          onClick={() => {
            onSort("created_at");
          }}
        >
          Created
          {getSortIcon("created_at")}
        </div>
        <div className="text-center">Actions</div>
      </div>

      {tenants.map((tenant) => (
        <div
          key={tenant.id}
          className={`hover:bg-muted/20 grid cursor-pointer ${columnsClass} items-center gap-3 border-t px-4 py-3`}
          style={{ minWidth: tableMinWidth }}
          onClick={() => {
            handleTenantClick(tenant);
          }}
        >
          <div className="flex min-w-0 items-center gap-3">
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={`${tenant.company} logo`}
                className="h-10 w-10 flex-shrink-0 rounded border object-contain p-1"
              />
            ) : (
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded border">
                <HarmonyLogoIcon className="text-muted-foreground h-6 w-6" />
              </div>
            )}
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold">{tenant.company}</p>
              <div className="text-muted-foreground flex items-center space-x-1 text-xs">
                <Globe className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{tenant.subdomain}</span>
              </div>
            </div>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <a
              href={`https://${tenant.subdomain}.${import.meta.env.VITE_HARMONY_URL}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 truncate text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
              onClick={(e) => {
                e.stopPropagation();
              }}
            >
              {tenant.subdomain}.{import.meta.env.VITE_HARMONY_URL}
            </a>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                void copyToClipboard(
                  `${tenant.subdomain}.${import.meta.env.VITE_HARMONY_URL}`,
                  `domain-${tenant.id}`,
                );
              }}
            >
              {copiedId === `domain-${tenant.id}` ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>

          <div className="flex min-w-0 items-center gap-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <p className="text-muted-foreground flex-1 truncate font-mono text-xs">
                  {tenant.id.length > 16
                    ? `${tenant.id.slice(0, 16)}...`
                    : tenant.id}
                </p>
              </TooltipTrigger>
              <TooltipContent>
                <p className="font-mono text-xs">{tenant.id}</p>
              </TooltipContent>
            </Tooltip>
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              onClick={(e) => {
                e.stopPropagation();
                void copyToClipboard(tenant.id, tenant.id);
              }}
            >
              {copiedId === tenant.id ? (
                <Check className="h-3 w-3 text-green-600" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </Button>
          </div>

          <div className="flex items-center">
            {tenant.industry ? (
              <Badge variant="outline" className="truncate text-xs">
                {tenant.industry.charAt(0).toUpperCase() +
                  tenant.industry.slice(1)}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">—</span>
            )}
          </div>

          <div className="flex items-center">
            {tenant.size ? (
              <Badge
                variant="secondary"
                className="flex items-center gap-1 text-xs"
              >
                <Users className="h-3 w-3" />
                {tenant.size}
              </Badge>
            ) : (
              <span className="text-muted-foreground text-xs">—</span>
            )}
          </div>

          {showCellColumns && (
            <>
              <div className="flex items-center">
                {tenant.data_residency ? (
                  <Badge variant="outline" className="text-xs">
                    {tenant.data_residency}
                  </Badge>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                )}
              </div>

              <div className="flex items-center">
                <span
                  className="text-muted-foreground truncate font-mono text-xs"
                  title={tenant.deployment_cell ?? undefined}
                >
                  {tenant.deployment_cell ?? "—"}
                </span>
              </div>
            </>
          )}

          <div className="flex items-center">
            <TrialStatusDot
              indicator={getTrialIndicator(
                tenant.trial_start_date,
                tenant.trial_end_date,
              )}
            />
          </div>

          <div className="flex items-center">
            <div className="text-muted-foreground flex items-center gap-1 text-xs">
              <Calendar className="h-3 w-3" />
              <span>{formatDate(tenant.created_at)}</span>
            </div>
          </div>

          <div
            className="flex items-center justify-center space-x-2"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0 hover:cursor-pointer"
                  onClick={() => {
                    setBrandingDialogTenant(tenant);
                  }}
                >
                  <Palette className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Portal branding</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-6 w-6 p-0 hover:cursor-pointer"
                  onClick={() => {
                    setLogoDialogTenant(tenant);
                  }}
                >
                  <Image className="h-3 w-3" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Manage logo</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <EditTenantDialog
                    tenant={tenant}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 w-6 p-0 hover:cursor-pointer"
                      >
                        <Edit className="h-3 w-3" />
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>Edit tenant</p>
              </TooltipContent>
            </Tooltip>
            <Tooltip>
              <TooltipTrigger asChild>
                <div>
                  <SsoSetupDialog
                    tenant={tenant}
                    trigger={
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-6 w-6 p-0 hover:cursor-pointer"
                      >
                        <Link className="h-3 w-3" />
                      </Button>
                    }
                  />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p>SSO setup</p>
              </TooltipContent>
            </Tooltip>
          </div>
        </div>
      ))}

      {logoDialogTenant && (
        <LogoManagementDialog
          tenant={logoDialogTenant}
          open={!!logoDialogTenant}
          onOpenChange={(open) => {
            if (!open) {
              setLogoDialogTenant(null);
            }
          }}
        />
      )}

      {brandingDialogTenant && (
        <PortalBrandingDialog
          tenant={brandingDialogTenant}
          open={!!brandingDialogTenant}
          onOpenChange={(open) => {
            if (!open) {
              setBrandingDialogTenant(null);
            }
          }}
        />
      )}
    </div>
  );
}
